import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { INTERNATIONAL_CODE, getCountryByCode, getPoliticalEntityLabel } from '../data/countries';
import { t } from '../i18n/translations';

const STORAGE_KEY = 'wymr_locale_prefs';

const defaultPrefs = {
  countryCode: INTERNATIONAL_CODE,
  regionId: 'all',
  language: 'en',
  includeInternational: true,
};

const LocaleContext = createContext(null);

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch (_) {}
  return defaultPrefs;
}

export function LocaleProvider({ children }) {
  const [prefs, setPrefsState] = useState(loadPrefs);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    document.documentElement.lang = prefs.language || 'en';
  }, [prefs]);

  const setPrefs = useCallback((updates) => {
    setPrefsState(prev => ({ ...prev, ...updates }));
  }, []);

  const country = getCountryByCode(prefs.countryCode);
  const politicalEntityLabel = getPoliticalEntityLabel(prefs.countryCode);

  const translate = useCallback((key) => t(key, prefs.language), [prefs.language]);

  const value = {
    ...prefs,
    country,
    politicalEntityLabel,
    setCountryCode: (code) => setPrefs({ countryCode: code }),
    setRegionId: (regionId) => setPrefs({ regionId }),
    setLanguage: (language) => setPrefs({ language }),
    setIncludeInternational: (includeInternational) => setPrefs({ includeInternational }),
    setPrefs,
    t: translate,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export default LocaleContext;
